/**
 * ⌘K / Ctrl+K: go to any page the sidebar offers (settings pages included), switch brand when
 * the account has more than one, and switch account when the user belongs to more than one.
 * Takes the menu the sidebar already filtered, so it never offers a page the user cannot open.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Building2, Check, Store } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { toast } from '@/components/ui/sonner';
import type { MenuItem } from '@/config/menu.config';
import { useUserAccounts } from '@/hooks/use-user-accounts';
import { useSwitchAccount } from '@/hooks/use-switch-account';
import { useBusinessUnit } from '@/hooks/use-business-units';
import { visibleBrands } from '@/components/layouts/account-menu';

const GROUP =
	'px-2 pb-2 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-gray-700 [&_[cmdk-group-heading]]:uppercase';
const ITEM = 'gap-3 rounded-lg px-2 py-1.5';

function Row({ icon, label, meta, current }: { icon: ReactNode; label: string; meta?: string; current?: boolean }) {
	return (
		<>
			<span className='flex size-7 shrink-0 items-center justify-center rounded-md border border-gray-500 bg-white text-gray-800 [&_svg]:size-3.5'>
				{icon}
			</span>
			<span className='min-w-0 flex-1 truncate text-body-sm text-ink'>{label}</span>
			{current ? (
				<span className='flex items-center gap-1 text-xs text-indigo-700'>
					<Check className='size-3.5 text-indigo-700' />
					Current
				</span>
			) : meta ? (
				<span className='truncate text-xs text-gray-700'>{meta}</span>
			) : null}
		</>
	);
}

const Key = ({ children }: { children: ReactNode }) => (
	<kbd className='inline-flex h-5 min-w-5 items-center justify-center rounded border border-gray-500 bg-white px-1 font-sans text-[11px] text-gray-800'>
		{children}
	</kbd>
);

export function CommandPalette({ items }: { items: MenuItem[] }) {
	const [open, setOpen] = useState(false);
	const navigate = useNavigate();
	const { accounts, currentAccountId } = useUserAccounts();
	const { switchAccount } = useSwitchAccount();
	const { buId, setBusinessUnit, businessUnits } = useBusinessUnit();
	const brands = visibleBrands(businessUnits ?? [], buId);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			// `key` is missing on some synthetic keydowns (e.g. Chrome autofill).
			if (event.key?.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
				event.preventDefault();
				setOpen((was) => !was);
			}
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, []);

	const run = (action: () => void) => {
		setOpen(false);
		action();
	};

	// As the user menu does it: a brand change stays in the account and goes home, since the
	// page you were on belongs to the brand you left.
	const selectBrand = (id: string, name: string) => {
		if (id === buId) return;
		setBusinessUnit(id);
		toast.success(`Now viewing ${name}`);
		navigate('/');
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogContent
				showCloseButton={false}
				className='top-[18%] max-w-[600px] translate-y-0 gap-0 overflow-hidden rounded-2xl bg-white p-0 backdrop-blur-none! sm:max-w-[600px]'
			>
				<DialogTitle className='sr-only'>Go to</DialogTitle>
				<DialogDescription className='sr-only'>Jump to a page, or switch brand or account</DialogDescription>
				{/* Plain substring match: fuzzy matching ranked "Response Strategy" above "Integrations" for "integ". */}
				<Command loop filter={(value, search) => (value.toLowerCase().includes(search.trim().toLowerCase()) ? 1 : 0)} className='bg-transparent'>
					<CommandInput placeholder='Search pages, brands and accounts…' wrapperClassName='h-12 border-gray-500 px-4' className='text-sm' />
					<CommandList className='max-h-[min(420px,60vh)]'>
						<CommandEmpty className='py-10 text-center text-body-sm text-gray-700'>Nothing matches that.</CommandEmpty>

						<CommandGroup heading='Go to' className={GROUP}>
							{items
								.filter((item) => !item.subItems?.length)
								.map((item) => (
									<CommandItem key={item.path} value={item.label} onSelect={() => run(() => navigate(item.path))} className={ITEM}>
										<Row icon={<item.icon />} label={item.label} />
									</CommandItem>
								))}
						</CommandGroup>

						{items
							.filter((item) => item.subItems?.length)
							.map((item) => (
								<CommandGroup key={item.label} heading={item.label} className={GROUP}>
									{item.subItems!.map((sub) => (
										<CommandItem
											key={sub.path}
											value={`${item.label} ${sub.label}`}
											onSelect={() => run(() => navigate(sub.path))}
											className={ITEM}
										>
											<Row icon={<item.icon />} label={sub.label} />
										</CommandItem>
									))}
								</CommandGroup>
							))}

						{brands.length > 1 && (
							<CommandGroup heading='Switch brand' className={GROUP}>
								{brands.map((brand) => (
									<CommandItem
										key={brand.id}
										value={`brand ${brand.name} ${brand.slug}`}
										onSelect={() => run(() => selectBrand(brand.id, brand.name))}
										className={ITEM}
									>
										<Row icon={<Store />} label={brand.name} current={brand.id === buId} meta='Brand' />
									</CommandItem>
								))}
							</CommandGroup>
						)}

						{accounts.length > 1 && (
							<CommandGroup heading='Switch account' className={GROUP}>
								{accounts.map((account) => (
									<CommandItem
										key={account.id}
										value={`account ${account.name} ${account.subDomain} ${account.id}`}
										onSelect={() => run(() => account.id !== currentAccountId && void switchAccount(account.id))}
										className={ITEM}
									>
										<Row icon={<Building2 />} label={account.name} current={account.id === currentAccountId} meta={account.subDomain} />
									</CommandItem>
								))}
							</CommandGroup>
						)}
					</CommandList>
				</Command>
				<div className='flex items-center gap-4 border-t border-gray-500 bg-gray-50 px-4 py-2 text-xs text-gray-700'>
					<span className='flex items-center gap-1.5'>
						<Key>↑</Key>
						<Key>↓</Key>
						to move
					</span>
					<span className='flex items-center gap-1.5'>
						<Key>↵</Key>
						to open
					</span>
					<span className='ml-auto flex items-center gap-1.5'>
						<Key>esc</Key>
						to close
					</span>
				</div>
			</DialogContent>
		</Dialog>
	);
}
